import { api } from '@/config/api';

export interface GoogleAdsAccount {
  _id: string;
  accountUid: string;
  companyUid: string;
  googleEmail: string;
  googleUserId?: string;
  customerId?: string;
  customerName?: string;
  currencyCode?: string;
  timeZone?: string;
  isManager: boolean;
  managerCustomerId?: string;
  isDefault: boolean;
  isActive: boolean;
  lastSyncedAt?: string;
  lastSyncError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GoogleAdsConnectionHealth {
  status: 'connected' | 'expired' | 'needs_account' | 'needs_setup' | 'unavailable';
  message: string;
  checkedAt: string;
}

export interface GoogleAdsCampaignSummary {
  id: string;
  name: string;
  status: string;
  advertisingChannelType?: string;
  startDate?: string;
  endDate?: string;
  advertisingChannelSubType?: string;
  /** Daily budget in currency micros. */
  budgetAmountMicros?: string;
  /** Campaign-total budget in currency micros (when the budget is not daily). */
  budgetTotalAmountMicros?: string;
  biddingStrategyType?: string;
  servingStatus?: string;
  /** ELIGIBLE / PAUSED / NOT_ELIGIBLE / LIMITED / … — why it isn't serving. */
  primaryStatus?: string;
  primaryStatusReasons?: string[];
  /** ISO when the CRM created it, else Google's CREATE change event (last 30 days, account time). */
  createdAt?: string;
  updatedAt?: string;
  createdVia?: 'crm' | 'google';
}

export interface GoogleAdsAdGroupRow {
  id: string;
  name: string;
  status: string;
  type?: string;
  campaignId: string;
  campaignName: string;
  campaignChannelType?: string;
  primaryStatus?: string;
  cpvBidMicros?: string;
  cpcBidMicros?: string;
}

export interface GoogleAdsAdRow {
  adId: string;
  adGroupId: string;
  adGroupName: string;
  status: string;
  type?: string;
  finalUrls: string[];
  headlines: string[];
  descriptions: string[];
  name?: string;
  /** APPROVED / APPROVED_LIMITED / DISAPPROVED / UNDER_REVIEW … */
  approvalStatus?: string;
  reviewStatus?: string;
  /** YouTube ids of the ad's videos; the first drives the thumbnail. */
  youtubeVideoIds?: string[];
  youtubeVideoId?: string;
  longHeadlines?: string[];
  businessName?: string;
  callToAction?: string;
}

/** One geoTargetConstants:suggest hit — Google targets by `id`, never by name. */
export interface GoogleAdsGeoSuggestion {
  id: string;
  name: string;
  canonicalName?: string;
  countryCode?: string;
  /** Country / City / Region / Postal Code / … */
  targetType?: string;
  reach?: number;
}

export interface GoogleAdsLanguage {
  id: string;
  code?: string;
  name: string;
  targetable: boolean;
}

const BASE = '/slighter_crm/google-ads';

export const googleAdsService = {
  config: async (): Promise<{ configured: boolean; live: boolean }> =>
    (await api.get(`${BASE}/config`)).data,

  oauthStart: async (): Promise<{ url: string; live: boolean }> =>
    (await api.get(`${BASE}/oauth/start`, { withCredentials: true })).data,

  list: async (): Promise<GoogleAdsAccount[]> =>
    (await api.get(`${BASE}/accounts`)).data,

  connectionHealth: async (accountUid: string): Promise<GoogleAdsConnectionHealth> =>
    (await api.get(`${BASE}/accounts/${accountUid}/health`)).data,

  remove: async (accountUid: string): Promise<{ ok: true }> =>
    (await api.delete(`${BASE}/accounts/${accountUid}`)).data,

  listAccessibleCustomers: async (accountUid: string): Promise<{
    customerIds: string[];
    customers?: Array<{ customerId: string; name: string; currency?: string; isManager: boolean; isTest: boolean }>;
    warning?: string;
  }> =>
    (await api.get(`${BASE}/accounts/${accountUid}/customers/accessible`)).data,

  /** One row of an MCC's client tree. */
  listManagerClients: async (accountUid: string, managerId: string): Promise<{
    managerId: string;
    clients: Array<{
      customerId: string; name: string; currency?: string;
      isManager: boolean; level: number; status?: string; isTest: boolean;
    }>;
  }> => (await api.get(`${BASE}/accounts/${accountUid}/customers/${managerId}/clients`)).data,

  attachCustomer: async (accountUid: string, customerId: string, loginCustomerId?: string): Promise<GoogleAdsAccount> =>
    (await api.post(`${BASE}/accounts/${accountUid}/customers/${customerId}/attach`, { loginCustomerId })).data,

  listCampaigns: async (accountUid: string): Promise<GoogleAdsCampaignSummary[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/campaigns`)).data,
  /** Ad-group / ad counts per campaign and ads per ad group — for list badges. */
  campaignCounts: async (accountUid: string): Promise<{ byCampaign: Record<string, { adGroups: number; ads: number; assetGroups?: number }>; byAdGroup: Record<string, number> }> =>
    (await api.get(`${BASE}/accounts/${accountUid}/campaigns/counts`)).data,

  pauseCampaign: async (accountUid: string, campaignId: string): Promise<{ ok: boolean }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/campaigns/${campaignId}/pause`)).data,

  resumeCampaign: async (accountUid: string, campaignId: string): Promise<{ ok: boolean }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/campaigns/${campaignId}/resume`)).data,

  createCampaign: async (accountUid: string, body: {
    name: string;
    advertisingChannelType: 'SEARCH' | 'DISPLAY' | 'VIDEO' | 'PERFORMANCE_MAX' | 'SHOPPING';
    dailyBudgetMicros: number;
    status?: 'ENABLED' | 'PAUSED';
    bidStrategy?: 'MANUAL_CPV' | 'TARGET_CPV' | 'TARGET_CPM' | 'MANUAL_CPC';
    budgetType?: 'DAILY' | 'TOTAL';
    startDate?: string;
    endDate?: string;
    videoPartners?: boolean;
    adFormats?: { inStream?: boolean; inFeed?: boolean; shorts?: boolean };
  }): Promise<{ campaignResourceName: string; budgetResourceName: string }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/campaigns`, body)).data,

  /** Location (ISO-2 codes and/or geo target ids) + language targeting on a campaign. */
  addCampaignCriteria: async (
    accountUid: string,
    campaignId: string,
    body: { countryCodes?: string[]; geoTargetIds?: Array<string | number>; languageIds?: Array<string | number> },
  ): Promise<{ added: number }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/campaigns/${campaignId}/criteria`, body)).data,

  /** Live location search (cities, regions, countries) → geo target constants. `country` = ISO-2 filter. */
  suggestGeo: async (accountUid: string, q: string, country?: string): Promise<GoogleAdsGeoSuggestion[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/geo/suggest`, { params: { q, ...(country ? { country } : {}) } })).data,

  /** Targetable language constants (backend caches them 24h), filtered by `q` when given. */
  listLanguages: async (accountUid: string, q?: string): Promise<GoogleAdsLanguage[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/languages`, { params: q ? { q } : undefined })).data,

  listAdGroups: async (accountUid: string, campaignId?: string): Promise<GoogleAdsAdGroupRow[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/ad-groups`, { params: campaignId ? { campaignId } : undefined })).data,

  listAds: async (accountUid: string, adGroupId?: string): Promise<GoogleAdsAdRow[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/ads`, { params: adGroupId ? { adGroupId } : undefined })).data,

  reusableAds: async (accountUid: string, adGroupId: string): Promise<Array<{
    adId: string; adGroupId: string; name: string; campaignName: string; adGroupName: string;
    finalUrls: string[]; descriptions: string[]; type: string;
  }>> => (await api.get(`${BASE}/accounts/${accountUid}/ad-groups/${adGroupId}/reusable-ads`)).data,

  reuseAd: async (accountUid: string, adGroupId: string, body: { sourceAdId: string; sourceAdGroupId: string }): Promise<{ resourceName: string }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ad-groups/${adGroupId}/reuse-ad`, body)).data,

  // Mutate ops — gated by Standard-Access Developer Token at runtime.
  createAdGroup: async (
    accountUid: string,
    body: {
      campaignId: string;
      name: string;
      cpcBidMicros?: number;
      cpvBidMicros?: number;
      adGroupType?: 'SEARCH_STANDARD' | 'VIDEO_TRUE_VIEW_IN_STREAM';
      status?: 'ENABLED' | 'PAUSED';
      optimizedTargetingEnabled?: boolean;
    },
  ): Promise<{ resourceName: string }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ad-groups`, body)).data,

  /** Video ad-group targeting: demographics, topics, YouTube/web placements, locations, languages. */
  addVideoTargeting: async (
    accountUid: string,
    adGroupId: string,
    body: {
      ageRanges?: string[]; genders?: string[]; topicIds?: number[];
      youtubeChannelIds?: string[]; youtubeVideoIds?: string[]; placementUrls?: string[];
      geoTargetIds?: Array<string | number>; languageIds?: Array<string | number>;
    },
  ): Promise<{ added: number }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ad-groups/${adGroupId}/video-targeting`, body)).data,

  /** YouTube in-stream video ad — the video must already be on the advertiser's YouTube channel. */
  createVideoAd: async (
    accountUid: string,
    body: {
      adGroupId: string;
      youtubeVideoId: string;
      finalUrl: string;
      actionButtonLabel?: string;
      actionHeadline?: string;
      status?: 'ENABLED' | 'PAUSED';
    },
  ): Promise<{ resourceName: string; assetResourceName: string }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ads/video`, body)).data,

  listKeywords: async (
    accountUid: string,
    adGroupId: string,
  ): Promise<Array<{ criterionId: string; text: string; matchType: string; status: string }>> =>
    (await api.get(`${BASE}/accounts/${accountUid}/ad-groups/${adGroupId}/keywords`)).data,

  addKeywords: async (
    accountUid: string,
    adGroupId: string,
    keywords: Array<{ text: string; matchType?: 'BROAD' | 'PHRASE' | 'EXACT' }>,
  ): Promise<{ added: number; resourceNames: string[] }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ad-groups/${adGroupId}/keywords`, { keywords })).data,

  pauseAdGroup: async (accountUid: string, adGroupId: string): Promise<{ ok: boolean }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ad-groups/${adGroupId}/pause`)).data,

  resumeAdGroup: async (accountUid: string, adGroupId: string): Promise<{ ok: boolean }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ad-groups/${adGroupId}/resume`)).data,

  createResponsiveSearchAd: async (
    accountUid: string,
    body: {
      adGroupId: string;
      finalUrls: string[];
      headlines: string[];
      descriptions: string[];
      status?: 'ENABLED' | 'PAUSED';
    },
  ): Promise<{ resourceName: string }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ads/responsive-search`, body)).data,

  pauseAd: async (accountUid: string, resourceName: string): Promise<{ ok: boolean }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ads/${encodeURIComponent(resourceName)}/pause`)).data,

  resumeAd: async (accountUid: string, resourceName: string): Promise<{ ok: boolean }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/ads/${encodeURIComponent(resourceName)}/resume`)).data,

  // ── Reporting (DB-backed; sync pulls from Google Ads via GAQL) ──
  syncReport: async (
    accountUid: string,
    body: { daysBack?: number; level?: GoogleAdsReportLevel },
  ): Promise<{ upserted: number; level: GoogleAdsReportLevel; days: number }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/reporting/sync`, body)).data,

  aggregateReport: async (
    accountUid: string,
    params: { level: GoogleAdsReportLevel; from?: string; to?: string },
  ): Promise<GoogleAdsAggregateRow[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/reporting/aggregate`, { params })).data,

  trendReport: async (
    accountUid: string,
    params: { level: GoogleAdsReportLevel; from?: string; to?: string },
  ): Promise<GoogleAdsTrendPoint[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/reporting/trend`, { params })).data,

  dailyReport: async (
    accountUid: string,
    params: { level: GoogleAdsReportLevel; entityId: string; from?: string; to?: string },
  ): Promise<GoogleAdsDailyRow[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/reporting/daily`, { params })).data,

  bulkDailyReport: async (
    accountUid: string,
    params: { level: GoogleAdsReportLevel; entityIds: string[]; from?: string; to?: string },
  ): Promise<Record<string, GoogleAdsTrendPoint[]>> => {
    if (params.entityIds.length === 0) return {};
    return (await api.get(`${BASE}/accounts/${accountUid}/reporting/bulk-daily`, {
      params: { ...params, entityIds: params.entityIds.join(',') },
    })).data;
  },

  backfillReport: async (
    accountUid: string,
    body: { days?: number },
  ): Promise<{ levels: Record<GoogleAdsReportLevel, { upserted: number }>; days: number }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/reporting/backfill`, body)).data,

  // ── Audiences (Customer Match) ──
  listAudiences: async (accountUid: string): Promise<GoogleAdsUserList[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/audiences`)).data,

  createAudienceFromGroup: async (
    accountUid: string,
    body: { groupUid: string; name: string; description?: string },
  ): Promise<{ userListResourceName: string; userListId: string; uploaded: number; operationsBatched: number }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/audiences/from-group`, body)).data,

  // ── Lead Forms ──
  listLeadForms: async (accountUid: string): Promise<GoogleAdsLeadFormSummary[]> =>
    (await api.get(`${BASE}/accounts/${accountUid}/lead-forms`)).data,

  createLeadForm: async (
    accountUid: string,
    body: GoogleAdsLeadFormCreatePayload,
  ): Promise<{ resourceName: string; id: string }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/lead-forms`, body)).data,

  /** Company admins only: webhook URL + account key for forms built in Google Ads. */
  getLeadWebhookSetup: async (accountUid: string): Promise<GoogleAdsLeadWebhookSetup> =>
    (await api.get(`${BASE}/accounts/${accountUid}/lead-forms/webhook`)).data,

  attachLeadForm: async (
    accountUid: string,
    assetId: string,
    body: { campaignId: string },
  ): Promise<{ resourceName: string }> =>
    (await api.post(`${BASE}/accounts/${accountUid}/lead-forms/${assetId}/attach`, body)).data,

  // ══════════════════════════════════════════════════════════════════════
  // YouTube (VIDEO) build path → backend google-ads/youtube/
  // The generic createCampaign/createAdGroup/addVideoTargeting/createVideoAd
  // above still work for video (they alias into the same service).
  // ══════════════════════════════════════════════════════════════════════
  youtube: {
    createDemandGen: async (accountUid: string, body: {
      name: string; dailyBudgetMicros: number; bidStrategy: 'MAXIMIZE_CLICKS' | 'MAXIMIZE_CONVERSIONS';
      startDate?: string; endDate?: string; formats: string[]; geoTargetIds: string[]; languageIds: string[];
      groups: Array<{ name: string; ads: Array<{ name: string; youtubeVideoId: string; logoUrl: string; finalUrl: string;
        businessName: string; headline: string; longHeadline: string; description: string }> }>;
    }): Promise<{ campaignResourceName: string }> => (await api.post(`${BASE}/youtube/accounts/${accountUid}/demand-gen`, body)).data,

    /** One ad group in an existing Demand Gen (YouTube) campaign. */
    createDemandGenAdGroup: async (accountUid: string, body: {
      campaignId: string; name: string; formats: string[]; geoTargetIds?: string[]; languageIds?: string[];
    }): Promise<{ resourceName: string; adGroupId: string }> =>
      (await api.post(`${BASE}/youtube/accounts/${accountUid}/demand-gen/ad-groups`, body)).data,

    /** One video ad in an existing Demand Gen ad group. */
    createDemandGenAd: async (accountUid: string, body: {
      adGroupId: string; name: string; youtubeVideoId: string; logoUrl: string; finalUrl: string;
      businessName: string; headline: string; longHeadline: string; description: string;
    }): Promise<{ resourceName: string }> =>
      (await api.post(`${BASE}/youtube/accounts/${accountUid}/demand-gen/ads`, body)).data,

    /** VIDEO campaign; locations/languages are applied right after create (best-effort → `targeting.error`). */
    createCampaign: async (accountUid: string, body: YouTubeCampaignCreatePayload): Promise<{
      campaignResourceName: string; budgetResourceName: string; targeting?: { added: number; error?: string };
    }> => (await api.post(`${BASE}/youtube/accounts/${accountUid}/campaigns`, body)).data,

    /** VIDEO_TRUE_VIEW_IN_STREAM ad group. */
    createAdGroup: async (accountUid: string, body: {
      campaignId: string; name: string; status?: 'ENABLED' | 'PAUSED'; cpvBidMicros?: number; optimizedTargetingEnabled?: boolean;
    }): Promise<{ resourceName: string }> =>
      (await api.post(`${BASE}/youtube/accounts/${accountUid}/ad-groups`, body)).data,

    addVideoTargeting: async (accountUid: string, adGroupId: string, body: {
      ageRanges?: string[]; genders?: string[]; topicIds?: number[];
      youtubeChannelIds?: string[]; youtubeVideoIds?: string[]; placementUrls?: string[];
      geoTargetIds?: Array<string | number>; languageIds?: Array<string | number>;
    }): Promise<{ added: number }> =>
      (await api.post(`${BASE}/youtube/accounts/${accountUid}/ad-groups/${adGroupId}/video-targeting`, body)).data,

    createVideoAd: async (accountUid: string, body: {
      adGroupId: string; youtubeVideoId: string; finalUrl: string;
      actionButtonLabel?: string; actionHeadline?: string; status?: 'ENABLED' | 'PAUSED';
    }): Promise<{ resourceName: string; assetResourceName: string }> =>
      (await api.post(`${BASE}/youtube/accounts/${accountUid}/ads/video`, body)).data,
  },

  // ══════════════════════════════════════════════════════════════════════
  // App Promotion (Google App campaigns) → backend google-ads/app-promotion/
  // ══════════════════════════════════════════════════════════════════════
  appPromotion: {
    listCampaigns: async (accountUid: string): Promise<GoogleAdsAppCampaign[]> =>
      (await api.get(`${BASE}/app-promotion/accounts/${accountUid}/campaigns`)).data,

    /** MULTI_CHANNEL / APP_CAMPAIGN with app settings, target CPA, daily budget, dates and campaign criteria. */
    createCampaign: async (accountUid: string, body: GoogleAdsAppCampaignCreatePayload): Promise<{
      campaignResourceName: string; campaignId: string; budgetResourceName: string;
      targeting?: { added: number; error?: string };
    }> => (await api.post(`${BASE}/app-promotion/accounts/${accountUid}/campaigns`, body)).data,

    /** Can the in-app bidding goals be used? Google needs ≥1 app conversion action on the account. */
    conversionReadiness: async (accountUid: string, appId?: string): Promise<{ inAppConversions: number | null; installConversions: number | null; sample: string[] }> =>
      (await api.get(`${BASE}/app-promotion/accounts/${accountUid}/conversion-readiness`, { params: { appId } })).data,

    /** Each App ad group holds exactly one App ad. */
    createAdGroup: async (accountUid: string, campaignId: string, body: { name: string; status?: 'ENABLED' | 'PAUSED' }): Promise<{
      resourceName: string; adGroupId: string;
    }> => (await api.post(`${BASE}/app-promotion/accounts/${accountUid}/campaigns/${campaignId}/ad-groups`, body)).data,

    /** AppAd: 2–5 headlines (≤30), 1–5 descriptions (≤90), image + YouTube asset resource names. */
    createAd: async (accountUid: string, adGroupId: string, body: {
      headlines: string[]; descriptions: string[]; images?: string[]; youtubeVideos?: string[]; status?: 'ENABLED' | 'PAUSED';
    }): Promise<{ resourceName: string }> =>
      (await api.post(`${BASE}/app-promotion/accounts/${accountUid}/ad-groups/${adGroupId}/ads`, body)).data,

    /** Exactly one of imageUrl (fetched server-side → bytes) or youtubeVideoId (id or URL). */
    createAsset: async (accountUid: string, body: { name?: string; imageUrl?: string; youtubeVideoId?: string }): Promise<GoogleAdsAssetRef> =>
      (await api.post(`${BASE}/app-promotion/accounts/${accountUid}/assets`, body)).data,
  },

  // ══════════════════════════════════════════════════════════════════════
  // App Promotion AI ASSISTANT → backend google-ads/app-promotion/ai/
  // Module-scoped: these routes, this session state and these prompts belong
  // to App Promotion only. Other modules' assistants get their own section.
  // ══════════════════════════════════════════════════════════════════════
  appPromotionAi: {
    /** Inspect the store listing and open the interview. */
    start: async (storeUrl: string): Promise<AppPromotionAiStartResult> =>
      (await api.post(`${BASE}/app-promotion/ai/start`, { storeUrl }, { timeout: 45_000 })).data,
    /** One interview turn. `answer` is a chip value or free text. */
    answer: async (sessionId: string, answer: string): Promise<AppPromotionAiAnswerResult> =>
      (await api.post(`${BASE}/app-promotion/ai/answer`, { sessionId, answer })).data,
    /** Interview → campaign draft (App Promotion DTO shape + resolved geo/language ids + rationale). */
    draft: async (body: { sessionId: string; accountUid?: string; currency?: string }): Promise<AppPromotionAiDraft> =>
      (await api.post(`${BASE}/app-promotion/ai/draft`, body, { timeout: 90_000 })).data,
    /** Headlines (≤30 chars) + descriptions (≤90 chars) within Google's AppAd limits. */
    creatives: async (body: {
      sessionId?: string;
      app?: { name?: string; description?: string; category?: string; store?: GoogleAdsAppStore; appId?: string };
      goal?: AppPromotionAiGoal; language?: string; headlineCount?: number; descriptionCount?: number;
    }): Promise<{ headlines: string[]; descriptions: string[]; creditsCharged: number }> =>
      (await api.post(`${BASE}/app-promotion/ai/creatives`, body, { timeout: 90_000 })).data,
    /** Image → enhanced variant, re-hosted on ImageKit. */
    enhanceImage: async (imageUrl: string, variant: AppPromotionAiEnhanceVariant): Promise<{
      url: string; width?: number; height?: number; variant: AppPromotionAiEnhanceVariant; label: string; model: string;
    }> => (await api.post(`${BASE}/app-promotion/ai/enhance-image`, { imageUrl, variant }, { timeout: 120_000 })).data,
  },

  // ══════════════════════════════════════════════════════════════════════
  // Objective build paths — one backend sub-module per campaign family,
  // mirrored by src/pages/google-ads/<objective>/ on this side.
  // ══════════════════════════════════════════════════════════════════════
  /** SEARCH campaigns → Leads + Website traffic. */
  search: {
    createCampaign: async (accountUid: string, body: SearchCampaignCreatePayload): Promise<SearchCampaignCreated> =>
      (await api.post(`${BASE}/search/accounts/${accountUid}/campaigns`, body)).data,
    createAdGroup: async (accountUid: string, body: SearchAdGroupCreatePayload): Promise<{
      resourceName: string; adGroupId: string; keywords?: TargetingOutcome; negatives?: TargetingOutcome;
    }> => (await api.post(`${BASE}/search/accounts/${accountUid}/ad-groups`, body)).data,
    createAd: async (accountUid: string, body: SearchRsaCreatePayload): Promise<{ resourceName: string }> =>
      (await api.post(`${BASE}/search/accounts/${accountUid}/ads/responsive-search`, body)).data,
    /** Campaign ids carrying a lead-form asset. */
    leadFormCampaigns: async (accountUid: string): Promise<string[]> =>
      (await api.get(`${BASE}/search/accounts/${accountUid}/lead-form-campaigns`)).data,
    /** An ad group's keywords with negatives kept apart. */
    adGroupKeywords: async (accountUid: string, adGroupId: string): Promise<{ keywords: SearchKeywordRow[]; negatives: SearchKeywordRow[] }> =>
      (await api.get(`${BASE}/search/accounts/${accountUid}/ad-groups/${adGroupId}/keywords`)).data,
    /** campaignId → objective for Search campaigns built in the CRM (untagged ones are inferred client-side). */
    campaignObjectives: async (accountUid: string): Promise<Record<string, SearchObjectiveKey>> =>
      (await api.get(`${BASE}/search/accounts/${accountUid}/campaign-objectives`)).data,
    /** Whether Google accepts conversion-based bidding on this account yet. */
    conversionReadiness: async (accountUid: string): Promise<SearchConversionReadiness> =>
      (await api.get(`${BASE}/search/accounts/${accountUid}/conversion-readiness`)).data,
  },

  /** Search (Leads + Website traffic) AI assistant — its own routes under /search/ai, separate from App Promotion's. */
  searchAi: {
    /** Read the business website and open the interview. */
    start: async (websiteUrl: string, objective: SearchObjectiveKey): Promise<SearchAiStartResult> =>
      (await api.post(`${BASE}/search/ai/start`, { websiteUrl, objective }, { timeout: 45_000 })).data,
    answer: async (sessionId: string, answer: string): Promise<SearchAiAnswerResult> =>
      (await api.post(`${BASE}/search/ai/answer`, { sessionId, answer })).data,
    /** Interview → whole campaign draft (settings, targeting, ad groups with keywords and ads). */
    draft: async (body: { sessionId: string; accountUid?: string; currency?: string }): Promise<SearchAiDraft> =>
      (await api.post(`${BASE}/search/ai/draft`, body, { timeout: 120_000 })).data,
    /** Headlines (≤30) + descriptions (≤90) + display paths for one ad group. */
    copy: async (body: SearchAiCopyRequest): Promise<SearchAiCopy> =>
      (await api.post(`${BASE}/search/ai/copy`, body, { timeout: 90_000 })).data,
    /** Keyword + negative ideas for one ad group. */
    keywords: async (body: SearchAiKeywordsRequest): Promise<SearchAiKeywordIdeas> =>
      (await api.post(`${BASE}/search/ai/keywords`, body, { timeout: 90_000 })).data,
  },

  /** DISPLAY campaigns → Awareness & reach. */
  display: {
    createCampaign: async (accountUid: string, body: DisplayCampaignCreatePayload): Promise<ObjectiveCampaignCreated> =>
      (await api.post(`${BASE}/display/accounts/${accountUid}/campaigns`, body)).data,
    createAdGroup: async (accountUid: string, body: DisplayAdGroupCreatePayload): Promise<{
      resourceName: string; adGroupId: string; targeting?: TargetingOutcome; keywords?: TargetingOutcome;
    }> => (await api.post(`${BASE}/display/accounts/${accountUid}/ad-groups`, body)).data,
    createAd: async (accountUid: string, body: ResponsiveDisplayAdCreatePayload): Promise<{ resourceName: string }> =>
      (await api.post(`${BASE}/display/accounts/${accountUid}/ads/responsive-display`, body)).data,
    createAsset: async (accountUid: string, body: { name?: string; imageUrl?: string; youtubeVideoId?: string }): Promise<GoogleAdsAssetRef> =>
      (await api.post(`${BASE}/display/accounts/${accountUid}/assets`, body)).data,
    /** Whether Google accepts conversion-based bidding on this account yet. */
    readiness: async (accountUid: string): Promise<ConversionReadiness> =>
      (await api.get(`${BASE}/display/accounts/${accountUid}/readiness`)).data,
    /** Responsive display ads with every image resolved to a URL. */
    listAds: async (accountUid: string, filter: { adGroupId?: string; campaignId?: string } = {}): Promise<DisplayAdRow[]> =>
      (await api.get(`${BASE}/display/accounts/${accountUid}/ads`, { params: filter })).data,
    /** An ad group's audience + content targeting, folded back from its criteria. */
    adGroupTargeting: async (accountUid: string, adGroupId: string): Promise<DisplayTargetingSummary> =>
      (await api.get(`${BASE}/display/accounts/${accountUid}/ad-groups/${adGroupId}/targeting`)).data,
  },

  /** PERFORMANCE_MAX campaigns → Sales + Local store visits. Asset groups replace ad groups + ads. */
  performanceMax: {
    createCampaign: async (accountUid: string, body: PmaxCampaignCreatePayload): Promise<ObjectiveCampaignCreated & {
      storeGoals?: { linked: number; error?: string };
      /** Local: which store / local-action goals were made biddable. */
      goals?: { biddable: string[]; note?: string; error?: string };
    }> => (await api.post(`${BASE}/performance-max/accounts/${accountUid}/campaigns`, body)).data,
    listAssetGroups: async (accountUid: string, campaignId?: string): Promise<GoogleAdsAssetGroupRow[]> =>
      (await api.get(`${BASE}/performance-max/accounts/${accountUid}/asset-groups`, { params: campaignId ? { campaignId } : undefined })).data,
    listAssetGroupAssets: async (accountUid: string, assetGroupId: string): Promise<GoogleAdsAssetGroupAsset[]> =>
      (await api.get(`${BASE}/performance-max/accounts/${accountUid}/asset-groups/${assetGroupId}/assets`)).data,
    /** Asset group atomically, then its audience signals (reported in `signals`, never blocking the group). */
    createAssetGroup: async (accountUid: string, body: AssetGroupCreatePayload): Promise<{ resourceName: string; assetGroupId: string; signals?: TargetingOutcome }> =>
      (await api.post(`${BASE}/performance-max/accounts/${accountUid}/asset-groups`, body)).data,
    pauseAssetGroup: async (accountUid: string, id: string): Promise<{ ok: true }> =>
      (await api.post(`${BASE}/performance-max/accounts/${accountUid}/asset-groups/${id}/pause`)).data,
    resumeAssetGroup: async (accountUid: string, id: string): Promise<{ ok: true }> =>
      (await api.post(`${BASE}/performance-max/accounts/${accountUid}/asset-groups/${id}/resume`)).data,
    listLocationAssetSets: async (accountUid: string): Promise<GoogleAdsLocationAssetSet[]> =>
      (await api.get(`${BASE}/performance-max/accounts/${accountUid}/location-asset-sets`)).data,
    createAsset: async (accountUid: string, body: { name?: string; imageUrl?: string; youtubeVideoId?: string }): Promise<GoogleAdsAssetRef> =>
      (await api.post(`${BASE}/performance-max/accounts/${accountUid}/assets`, body)).data,
    /** Conversion tracking + linked store locations. */
    readiness: async (accountUid: string): Promise<PmaxReadiness> =>
      (await api.get(`${BASE}/performance-max/accounts/${accountUid}/readiness`)).data,
    /** campaignId → objective ('sales' | 'local'), bid targets, linked location sets. */
    campaignMeta: async (accountUid: string): Promise<Record<string, PmaxCampaignMeta>> =>
      (await api.get(`${BASE}/performance-max/accounts/${accountUid}/campaign-meta`)).data,
    /** First image / logo / headline of every asset group. */
    assetGroupPreviews: async (accountUid: string): Promise<PmaxAssetGroupPreview[]> =>
      (await api.get(`${BASE}/performance-max/accounts/${accountUid}/asset-group-previews`)).data,
  },

  /** SHOPPING campaigns → Product sales. Needs a Merchant Center linked in Google Ads. */
  shopping: {
    merchantCenters: async (accountUid: string): Promise<{ links: Array<{ merchantId: string; linkId: string }>; warning?: string }> =>
      (await api.get(`${BASE}/shopping/accounts/${accountUid}/merchant-centers`)).data,
    createCampaign: async (accountUid: string, body: ShoppingCampaignCreatePayload): Promise<ObjectiveCampaignCreated> =>
      (await api.post(`${BASE}/shopping/accounts/${accountUid}/campaigns`, body)).data,
    createAdGroup: async (accountUid: string, body: ShoppingAdGroupCreatePayload): Promise<{
      resourceName: string; adGroupId: string; listingGroup: { ok: boolean; error?: string }; ad?: { resourceName?: string; error?: string };
    }> => (await api.post(`${BASE}/shopping/accounts/${accountUid}/ad-groups`, body)).data,
    createAd: async (accountUid: string, body: { adGroupId: string; status?: 'ENABLED' | 'PAUSED' }): Promise<{ resourceName: string }> =>
      (await api.post(`${BASE}/shopping/accounts/${accountUid}/ads/product`, body)).data,
    /** Conversion tracking + linked Merchant Center accounts. */
    readiness: async (accountUid: string): Promise<ShoppingReadiness> =>
      (await api.get(`${BASE}/shopping/accounts/${accountUid}/readiness`)).data,
    /** campaignId → Merchant Center, feed label, priority, local inventory, bid targets. */
    campaignMeta: async (accountUid: string): Promise<Record<string, ShoppingCampaignMeta>> =>
      (await api.get(`${BASE}/shopping/accounts/${accountUid}/campaign-meta`)).data,
    /** An ad group's product groups (listing groups). */
    productGroups: async (accountUid: string, adGroupId: string): Promise<ShoppingProductGroup[]> =>
      (await api.get(`${BASE}/shopping/accounts/${accountUid}/ad-groups/${adGroupId}/product-groups`)).data,
  },
};

// ── Objective payloads (kept in step with backend google-ads/<objective>/dto) ──

export interface TargetingOutcome { added: number; error?: string }
export interface ObjectiveCampaignCreated {
  campaignResourceName: string; campaignId: string; budgetResourceName: string; targeting?: TargetingOutcome;
}
export interface CampaignTargetingInput {
  countryCodes?: string[];
  geoTargetIds?: Array<string | number>;
  languageIds?: Array<string | number>;
  startDate?: string;
  endDate?: string;
}

export type SearchBidding = 'MANUAL_CPC' | 'MAXIMIZE_CLICKS' | 'MAXIMIZE_CONVERSIONS' | 'MAXIMIZE_CONVERSION_VALUE';
export type SearchObjectiveKey = 'leads' | 'traffic';
export type SearchMatchType = 'BROAD' | 'PHRASE' | 'EXACT';
export interface SearchKeywordRow { criterionId: string; text: string; matchType: string; status: string }
export interface SearchCampaignCreatePayload extends CampaignTargetingInput {
  name: string; dailyBudgetMicros: number; status?: 'ENABLED' | 'PAUSED'; bidding: SearchBidding;
  cpcBidCeilingMicros?: number; targetCpaMicros?: number; targetRoas?: number;
  searchPartners?: boolean; displayNetwork?: boolean;
  /** Which workspace built it, so Leads and Website traffic each list their own. */
  objective?: SearchObjectiveKey;
  /** Lead-form asset attached in the same atomic create (Leads). */
  leadFormAssetId?: string;
}
export interface SearchCampaignCreated extends ObjectiveCampaignCreated { leadForm?: { attached: boolean } }
export interface SearchConversionReadiness {
  trackingStatus: string | null;
  enabledConversionActions: number | null;
  /** false = Google refuses Maximize conversions / conversion value until conversion tracking exists; null = unknown. */
  conversionBiddingAllowed: boolean | null;
}

export interface SearchAiSite {
  url: string; host: string; siteName: string | null; title: string | null; description: string | null;
  iconUrl: string | null; imageUrl: string | null; language: string | null; headings: string[]; offers: string[];
  phones: string[]; whatsapp: string | null; countryHint: string | null; text: string; links: Array<{ url: string; text: string }>;
  /** Set when the site could not be read (bot protection, offline) — the draft is written from the answers alone. */
  readError?: string;
}
export interface SearchAiQuestion {
  key: 'offer' | 'markets' | 'budget' | 'language';
  prompt: string;
  hint?: string;
  options?: Array<{ value: string; label: string; description?: string; recommended?: boolean }>;
  allowFreeText: boolean;
  placeholder?: string;
  progress: { index: number; total: number };
}
export interface SearchAiStartResult { sessionId: string; site: SearchAiSite; question: SearchAiQuestion }
export interface SearchAiAnswerResult { question: SearchAiQuestion | null; done: boolean; acknowledged?: string }
export interface SearchAiDraftGroup {
  name: string;
  keywords: Array<{ text: string; matchType: SearchMatchType }>;
  negatives: string[];
  ad: { finalUrl: string; headlines: string[]; descriptions: string[]; path1: string; path2: string };
}
export interface SearchAiDraft {
  campaign: {
    name: string; objective: SearchObjectiveKey; bidding: SearchBidding; dailyBudgetMicros: number;
    startDate: string; endDate: string | null; searchPartners: boolean; displayNetwork: boolean;
  };
  targeting: {
    geo: Array<{ id: string; name: string; canonicalName?: string; countryCode?: string; targetType?: string }>;
    languages: Array<{ id: string; name: string; code: string }>;
    unresolved: string[];
  };
  groups: SearchAiDraftGroup[];
  rationale: Record<string, string>;
  creditsCharged: number;
}
export interface SearchAiCopyRequest {
  sessionId?: string; objective: SearchObjectiveKey; websiteUrl?: string; groupName?: string; keywords?: string[];
  existing?: string[]; language?: string; headlineCount?: number; descriptionCount?: number;
}
export interface SearchAiCopy { headlines: string[]; descriptions: string[]; path1: string; path2: string; creditsCharged: number }
export interface SearchAiKeywordsRequest {
  sessionId?: string; objective: SearchObjectiveKey; websiteUrl?: string; groupName?: string; existing?: string[]; places?: string[]; language?: string;
}
export interface SearchAiKeywordIdeas { keywords: Array<{ text: string; matchType: SearchMatchType }>; negatives: string[]; creditsCharged: number }
export interface SearchKeywordInput { text: string; matchType?: 'BROAD' | 'PHRASE' | 'EXACT' }
export interface SearchAdGroupCreatePayload {
  campaignId: string; name: string; status?: 'ENABLED' | 'PAUSED'; cpcBidMicros?: number;
  keywords?: SearchKeywordInput[]; negativeKeywords?: SearchKeywordInput[];
}
export interface SearchRsaCreatePayload {
  adGroupId: string; finalUrls: string[]; headlines: string[]; descriptions: string[];
  path1?: string; path2?: string; status?: 'ENABLED' | 'PAUSED';
}

/** Same shape for every objective module's readiness check. */
export interface ConversionReadiness {
  trackingStatus: string | null;
  enabledConversionActions: number | null;
  /** false = Google refuses conversion-based bidding until conversion tracking exists; null = unknown. */
  conversionBiddingAllowed: boolean | null;
}

export type DisplayBidding = 'MANUAL_CPM' | 'MAXIMIZE_CLICKS' | 'MANUAL_CPC' | 'MAXIMIZE_CONVERSIONS';
export interface DisplayImageRef { resourceName: string; url?: string; width?: number; height?: number; name?: string }
export interface DisplayAdRow {
  adId: string; adGroupId: string; adGroupName: string; campaignId: string; status: string;
  name?: string; finalUrls: string[]; businessName?: string;
  headlines: string[]; longHeadline?: string; descriptions: string[]; callToActionText?: string;
  marketingImages: DisplayImageRef[]; squareImages: DisplayImageRef[]; logos: DisplayImageRef[]; squareLogos: DisplayImageRef[];
  youtubeVideoIds: string[];
  approvalStatus?: string; reviewStatus?: string; adStrength?: string;
}
export interface DisplayTargetingSummary {
  /** Included buckets; null = no restriction. */
  ages: string[] | null;
  genders: string[] | null;
  topics: Array<{ id: string; name: string }>;
  keywords: string[];
  placements: string[];
  youtubeChannels: string[];
  youtubeVideos: string[];
  optimizedTargeting: boolean | null;
  cpcBidMicros?: string;
  cpmBidMicros?: string;
}
export interface DisplayCampaignCreatePayload extends CampaignTargetingInput {
  name: string; dailyBudgetMicros: number; status?: 'ENABLED' | 'PAUSED'; bidding: DisplayBidding;
  cpcBidCeilingMicros?: number; targetCpaMicros?: number;
}
export interface DisplayTargetingInput {
  ageRanges?: string[]; genders?: string[]; topicIds?: number[]; placementUrls?: string[];
  youtubeChannelIds?: string[]; youtubeVideoIds?: string[]; keywords?: string[];
}
export interface DisplayAdGroupCreatePayload {
  campaignId: string; name: string; status?: 'ENABLED' | 'PAUSED'; cpcBidMicros?: number; cpmBidMicros?: number;
  optimizedTargetingEnabled?: boolean; targeting?: DisplayTargetingInput;
}
export interface ResponsiveDisplayAdCreatePayload {
  adGroupId: string; finalUrls: string[]; businessName: string; headlines: string[]; longHeadline: string; descriptions: string[];
  marketingImages: string[]; squareMarketingImages: string[]; logoImages?: string[]; squareLogoImages?: string[];
  youtubeVideos?: string[]; callToActionText?: string; status?: 'ENABLED' | 'PAUSED';
}

export type PmaxBidding = 'MAXIMIZE_CONVERSIONS' | 'MAXIMIZE_CONVERSION_VALUE';
export type PmaxObjectiveKey = 'sales' | 'local';
export interface PmaxCampaignCreatePayload extends CampaignTargetingInput {
  name: string; dailyBudgetMicros: number; status?: 'ENABLED' | 'PAUSED'; bidding: PmaxBidding;
  targetCpaMicros?: number; targetRoas?: number; locationAssetSetIds?: string[];
  /** Which workspace built it, so Sales and Local each list their own. */
  objective?: PmaxObjectiveKey;
  /** false = opt out of Final URL expansion (Google's default is on). */
  finalUrlExpansion?: boolean;
}
export interface AssetGroupCreatePayload {
  campaignId: string; name: string; finalUrls: string[]; status?: 'ENABLED' | 'PAUSED'; businessName: string;
  headlines: string[]; longHeadlines: string[]; descriptions: string[];
  marketingImages: string[]; squareMarketingImages: string[]; logoImages: string[]; landscapeLogoImages?: string[];
  portraitMarketingImages?: string[]; youtubeVideos?: string[]; callToAction?: string; path1?: string; path2?: string;
  /** Audience signals. */
  searchThemes?: string[]; audienceUserLists?: string[];
}
export interface PmaxReadiness { conversions: ConversionReadiness; locationSets: GoogleAdsLocationAssetSet[] | null }
export interface PmaxCampaignMeta {
  objective: PmaxObjectiveKey; tagged: boolean; targetCpaMicros?: string; targetRoas?: number; locationSets: number;
}
export interface PmaxAssetGroupPreview { assetGroupId: string; campaignId: string; imageUrl?: string; logoUrl?: string; headline?: string }
export interface GoogleAdsAssetGroupAsset {
  assetId: string; fieldType: string; type: string; name?: string; text?: string;
  imageUrl?: string; width?: number; height?: number; youtubeVideoId?: string; youtubeVideoTitle?: string;
  status: string; source?: string; primaryStatus?: string;
}
export interface GoogleAdsAssetGroupRow {
  id: string; name: string; status: string; campaignId: string; campaignName: string; finalUrls: string[]; primaryStatus?: string;
  /** Google's own rating: PENDING / NO_ADS / POOR / AVERAGE / GOOD / EXCELLENT. */
  adStrength?: string;
}
export interface GoogleAdsLocationAssetSet { id: string; name: string; type: string; status: string }

export type ShoppingBidding = 'MANUAL_CPC' | 'MAXIMIZE_CLICKS' | 'TARGET_ROAS';
export interface ShoppingCampaignCreatePayload extends CampaignTargetingInput {
  name: string; dailyBudgetMicros: number; status?: 'ENABLED' | 'PAUSED'; merchantId: string; feedLabel?: string;
  campaignPriority?: 0 | 1 | 2; enableLocal?: boolean; searchPartners?: boolean;
  bidding: ShoppingBidding; cpcBidCeilingMicros?: number; targetRoas?: number;
}
export interface ShoppingMerchantLink { merchantId: string; linkId: string }
export interface ShoppingReadiness { conversions: ConversionReadiness; merchantCenters: { links: ShoppingMerchantLink[]; warning?: string } }
export interface ShoppingCampaignMeta {
  merchantId?: string; feedLabel?: string; campaignPriority?: number; enableLocal?: boolean; targetRoas?: number; cpcBidCeilingMicros?: string;
}
export interface ShoppingProductGroup { criterionId: string; type: string; status: string; negative: boolean; cpcBidMicros?: string; label: string }
export interface ShoppingAdGroupCreatePayload {
  campaignId: string; name: string; status?: 'ENABLED' | 'PAUSED'; cpcBidMicros?: number; withProductAd?: boolean;
}

export interface YouTubeCampaignCreatePayload {
  name: string;
  dailyBudgetMicros: number;
  status?: 'ENABLED' | 'PAUSED';
  bidStrategy?: 'MANUAL_CPV' | 'TARGET_CPV' | 'TARGET_CPM';
  budgetType?: 'DAILY' | 'TOTAL';
  startDate?: string;
  endDate?: string;
  videoPartners?: boolean;
  adFormats?: { inStream?: boolean; inFeed?: boolean; shorts?: boolean };
  geoTargetIds?: Array<string | number>;
  languageIds?: Array<string | number>;
}

export type GoogleAdsAppStore = 'GOOGLE_APP_STORE' | 'APPLE_APP_STORE';
export type GoogleAdsAppBiddingGoal =
  | 'OPTIMIZE_INSTALLS_TARGET_INSTALL_COST'
  | 'OPTIMIZE_IN_APP_CONVERSIONS_TARGET_INSTALL_COST'
  | 'OPTIMIZE_IN_APP_CONVERSIONS_TARGET_CONVERSION_COST';

export interface GoogleAdsAppCampaignCreatePayload {
  registryAppUid?: string;
  name: string;
  appId: string;
  appStore: GoogleAdsAppStore;
  biddingStrategyGoalType: GoogleAdsAppBiddingGoal;
  targetCpaMicros: number;
  dailyBudgetMicros: number;
  status?: 'ENABLED' | 'PAUSED';
  startDate?: string;
  endDate?: string;
  geoTargetIds?: Array<string | number>;
  languageIds?: Array<string | number>;
}

// ── App Promotion AI assistant ──────────────────────────────────────────
export type AppPromotionAiGoal = 'installs' | 'ad_revenue' | 'in_app_purchases' | 'specific_users';
export type AppPromotionAiEnhanceVariant = 'clean_background' | 'landscape_1_91' | 'square_1_1' | 'device_frame';

export interface AppPromotionAiApp {
  store: GoogleAdsAppStore;
  appId: string;
  storeUrl: string;
  name: string | null;
  description: string | null;
  iconUrl: string | null;
  screenshots: string[];
  category: string | null;
  rating: number | null;
  developer: string | null;
}

export interface AppPromotionAiQuestion {
  key: 'goal' | 'markets' | 'budget' | 'language';
  prompt: string;
  hint?: string;
  options?: Array<{ value: string; label: string; description?: string; recommended?: boolean }>;
  allowFreeText: boolean;
  placeholder?: string;
  progress: { index: number; total: number };
}

export interface AppPromotionAiStartResult { sessionId: string; app: AppPromotionAiApp; question: AppPromotionAiQuestion }
export interface AppPromotionAiAnswerResult { question: AppPromotionAiQuestion | null; done: boolean; acknowledged?: string }

export interface AppPromotionAiDraft {
  campaign: {
    name: string;
    appStore: GoogleAdsAppStore;
    appId: string;
    biddingStrategyGoalType: GoogleAdsAppBiddingGoal;
    targetCpaMicros: number;
    dailyBudgetMicros: number;
    startDate: string;
    endDate: string | null;
    countryCodes: string[];
    languageCodes: string[];
  };
  targeting: {
    geoTargetIds: string[];
    languageIds: string[];
    geo: Array<{ id: string; name: string; countryCode: string }>;
    languages: Array<{ id: string; name: string; code: string }>;
    unresolvedCountries: string[];
  };
  rationale: Record<string, string>;
  creditsCharged: number;
}

export interface GoogleAdsAppCampaign {
  id: string;
  resourceName: string;
  name: string;
  status: string;
  appId?: string;
  appStore?: string;
  biddingStrategyGoalType?: string;
  biddingStrategyType?: string;
  targetCpaMicros?: string;
  budgetAmountMicros?: string;
  startDate?: string;
  endDate?: string;
  servingStatus?: string;
  primaryStatus?: string;
  primaryStatusReasons?: string[];
}

export interface GoogleAdsAssetRef {
  resourceName: string;
  id: string;
  type: 'IMAGE' | 'YOUTUBE_VIDEO';
  reused: boolean;
}

export type GoogleAdsLeadFormFieldType =
  | 'FULL_NAME' | 'FIRST_NAME' | 'LAST_NAME'
  | 'EMAIL' | 'PHONE_NUMBER'
  | 'POSTAL_CODE' | 'STREET_ADDRESS' | 'CITY' | 'REGION' | 'COUNTRY'
  | 'COMPANY_NAME' | 'JOB_TITLE' | 'WORK_EMAIL' | 'WORK_PHONE';

export type GoogleAdsLeadFormCtaType =
  | 'LEARN_MORE' | 'GET_QUOTE' | 'APPLY_NOW' | 'SIGN_UP'
  | 'CONTACT_US' | 'SUBSCRIBE' | 'DOWNLOAD' | 'BOOK_NOW' | 'GET_OFFER';

export interface GoogleAdsLeadFormCreatePayload {
  name: string;
  businessName: string;
  callToActionType: GoogleAdsLeadFormCtaType;
  callToActionDescription?: string;
  headline: string;
  description: string;
  privacyPolicyUrl: string;
  fields: GoogleAdsLeadFormFieldType[];
  postSubmitHeadline: string;
  postSubmitDescription: string;
  postSubmitCallToActionText?: string;
}

/** Where a form's submissions go, read from Google's own delivery settings. */
export type GoogleAdsLeadFormDelivery = 'connected' | 'key_mismatch' | 'legacy_url' | 'other_destination' | 'no_webhook' | 'unknown';

export interface GoogleAdsLeadFormSummary {
  resourceName: string;
  id: string;
  name: string;
  businessName?: string;
  headline?: string;
  description?: string;
  callToActionType?: string;
  privacyPolicyUrl?: string;
  fields?: string[];
  delivery?: GoogleAdsLeadFormDelivery;
}

export interface GoogleAdsLeadWebhookSetup {
  url?: string;
  key?: string;
  problems: string[];
  lastLeadAt?: string;
  lastTestAt?: string;
  lastErrorAt?: string;
  lastError?: string;
}

export type GoogleAdsReportLevel = 'campaign' | 'adgroup' | 'ad';

export interface GoogleAdsAggregateRow {
  _id: string;            // entityId
  entityName?: string;
  impressions: number;
  clicks: number;
  cost: number;            // currency units (already converted from micros)
  conversions: number;
  conversionsValue: number;
}

export interface GoogleAdsUserList {
  resourceName: string;
  id: string;
  name: string;
  description?: string;
  membershipLifeSpan?: number;
  sizeForDisplay?: number;
  sizeForSearch?: number;
  matchRatePercentage?: number;
  status?: string;
}

export interface GoogleAdsTrendPoint {
  date: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  conversionsValue: number;
}

export interface GoogleAdsDailyRow {
  date: string;            // YYYY-MM-DD
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;             // percent (e.g. 3.45 = 3.45%)
  averageCpc: number;
  averageCpm: number;
  conversionsValue: number;
}
